import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar, ArrowRight, Archive } from "lucide-react";
import type { BlogPost } from "@shared/schema";
import { updatePageSEO } from "@/lib/seo";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";

const Blog = () => {
  useEffect(() => {
    updatePageSEO({
      title: "The Corso Papers - Diane Corso's Horror Writing Blog",
      description: "Behind-the-scenes insights into Diane Corso's horror writing process, updates on Broken Things, horror writing tips, and musings from the dark side of creativity.",
      keywords: "Diane Corso blog, Diane Corso Author, horror writing tips, Broken Things updates, The Corso Papers, author blog",
      ogImage: "https://www.dianecorsofiction.com/attached_assets/broken things book rev (1)_1756509124921.webp",
      canonical: "https://www.dianecorsofiction.com/blog"
    });
  }, []);
  const { data: posts = [], isLoading } = useQuery<BlogPost[]>({
    queryKey: ["/api/blog"],
  });

  // Get top 4 most viewed posts (for now, use the 4 most recent as placeholder)
  // In a real implementation, you'd track views and sort by that
  const mostViewedPosts = posts.slice(0, 4);
  const regularPosts = posts.slice(4);

  if (isLoading) {
    return (
      <div className="min-h-screen pt-16 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blood-red border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gothic-white">Loading blog posts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-16">
      <section className="py-20 bg-gothic-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-5xl font-cinzel font-bold text-blood-red mb-6">The Corso Papers</h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Behind-the-scenes insights, horror writing tips, and updates from the dark side of creativity
            </p>
            <div className="w-24 h-1 bg-blood-red mx-auto mt-6"></div>
          </div>

          {posts.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-xl text-gray-300 mb-4">No blog posts available yet.</p>
              <p className="text-gray-400">Check back soon for updates from Diane's writing journey.</p>
            </div>
          ) : (
            <>
              {/* Most Viewed Posts Carousel */}
              {mostViewedPosts.length > 0 && (
                <div className="mb-16">
                  <h2 className="text-3xl font-playfair font-bold text-blood-red mb-8 text-center">
                    Most Viewed Posts
                  </h2>
                  <Carousel
                    opts={{
                      align: "start",
                      loop: true,
                    }}
                    className="w-full max-w-6xl mx-auto px-4 md:px-0"
                  >
                    <CarouselContent className="-ml-2 md:-ml-4">
                      {mostViewedPosts.map((post) => (
                        <CarouselItem key={post.id} className="pl-2 md:pl-4 basis-[85%] sm:basis-[70%] md:basis-1/2 lg:basis-1/3">
                          <Card className="bg-gothic-charcoal border-gothic-gray hover:transform hover:scale-105 transition-all duration-300 overflow-hidden shadow-2xl h-full">
                            {post.featuredImage && (
                              <img 
                                src={post.featuredImage} 
                                alt={`${post.title} featured image`}
                                className="w-full h-48 object-cover" 
                                loading="lazy"
                                decoding="async"
                                width="600"
                                height="300"
                              />
                            )}
                            <CardContent className="p-6">
                              <div className="flex items-center text-sm text-gray-400 mb-3">
                                <Calendar className="w-4 h-4 mr-2" />
                                <span>{new Date(post.publishedAt!).toLocaleDateString('en-US', { 
                                  year: 'numeric', 
                                  month: 'long', 
                                  day: 'numeric' 
                                })}</span>
                              </div>
                              <h3 className="text-xl font-playfair font-bold mb-3 text-gothic-white line-clamp-2">
                                {post.title}
                              </h3>
                              <p className="text-gray-300 mb-4 leading-relaxed line-clamp-3">
                                {post.excerpt}
                              </p>
                              <a 
                                href={`/blog/${post.slug}`} 
                                className="inline-flex items-center text-blood-red hover:text-gothic-white font-semibold transition-colors duration-300"
                                aria-label={`Read full post: ${post.title}`}
                              >
                                Read Full Post <ArrowRight className="ml-1" size={16} />
                              </a>
                            </CardContent>
                          </Card>
                        </CarouselItem>
                      ))}
                    </CarouselContent>
                    <CarouselPrevious className="hidden md:flex" />
                    <CarouselNext className="hidden md:flex" />
                    
                    {/* Pagination Dots for Mobile */}
                    <div className="flex justify-center gap-2 mt-6 md:hidden">
                      {mostViewedPosts.map((_, index) => (
                        <div 
                          key={index}
                          className="w-2 h-2 rounded-full bg-gray-600"
                          aria-label={`Slide ${index + 1}`}
                        />
                      ))}
                    </div>
                  </Carousel>
                  
                  {/* Swipe hint for mobile */}
                  <p className="text-center text-sm text-gray-500 mt-4 md:hidden">
                    Swipe to explore more posts →
                  </p>
                </div>
              )}

              {/* Recent Posts Grid */}
              <h2 className="text-3xl font-playfair font-bold text-blood-red mb-8 text-center">
                Recent Posts
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {regularPosts.map((post) => (
                <Card key={post.id} className="bg-gothic-charcoal border-gothic-gray hover:transform hover:scale-105 transition-all duration-300 overflow-hidden shadow-2xl">
                  {post.featuredImage && (
                    <img 
                      src={post.featuredImage} 
                      alt={`${post.title} featured image`}
                      className="w-full h-48 object-cover" 
                      data-testid={`blog-image-${post.slug}`}
                      loading="lazy"
                      decoding="async"
                      width="600"
                      height="300"
                    />
                  )}
                  <CardContent className="p-6">
                    <div className="flex items-center text-sm text-gray-400 mb-3">
                      <Calendar className="w-4 h-4 mr-2" />
                      <span>{new Date(post.publishedAt!).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}</span>
                    </div>
                    <h2 className="text-xl font-playfair font-bold mb-3 text-gothic-white line-clamp-2">
                      {post.title}
                    </h2>
                    <p className="text-gray-300 mb-4 leading-relaxed line-clamp-3">
                      {post.excerpt}
                    </p>
                    <a 
                      href={`/blog/${post.slug}`} 
                      className="inline-flex items-center text-blood-red hover:text-gothic-white font-semibold transition-colors duration-300"
                      data-testid={`read-post-${post.slug}`}
                      aria-label={`Read full post: ${post.title}`}
                    >
                      Read Full Post: {post.title} <ArrowRight className="ml-1" size={16} />
                    </a>
                  </CardContent>
                </Card>
              ))}
            </div>

              {/* Archive Button */}
              <div className="mt-12 text-center">
                <Button 
                  asChild
                  variant="outline"
                  className="border-blood-red text-blood-red hover:bg-blood-red hover:text-white transition-colors duration-300"
                >
                  <a href="/blog/archive" aria-label="View all blog posts in archive">
                    <Archive className="mr-2" size={16} />
                    View All Posts in Archive
                  </a>
                </Button>
              </div>
            </>
          )}

          {/* Categories/Tags section could go here */}
          <div className="mt-16 text-center">
            <h3 className="text-2xl font-playfair font-bold text-blood-red mb-6">Popular Topics</h3>
            <div className="flex flex-wrap justify-center gap-3">
              {["Horror Writing Tips", "Book Updates", "Behind the Scenes", "Research", "Events", "Author Life"].map((topic) => (
                <span 
                  key={topic}
                  className="px-4 py-2 bg-gothic-charcoal border border-gothic-gray rounded-full text-gray-300 hover:border-blood-red hover:text-blood-red transition-colors duration-300 cursor-pointer"
                  data-testid={`topic-${topic.toLowerCase().replace(' ', '-')}`}
                >
                  {topic}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Blog;